import { z } from 'zod';
import { normalizeOptionalBuildTemplateId } from '@/IsekaiRPG/modules/character/characterSemantic';

import { CharacterSeedDefinitionSchema } from './characterSeedDefinition';
import { CharacterPatchSchema } from './characterPatch';
import { CharacterPresenceSchema } from './characterPresence';
import { CharacterProfileSchema } from './characterProfile';
import { CharacterCompositionSeedSchema } from './composition';
import {
  CharacterSeedEntityOverridesSchema,
  CharacterSeedProfileOverridesSchema,
} from './characterSpawnOverrides';

// ============================================
// 生成调度请求
// ============================================

export const CharacterSpawnAIDetailLevelSchema = z.enum(['minimal', 'standard', 'full']);
export type CharacterSpawnAIDetailLevel = z.infer<typeof CharacterSpawnAIDetailLevelSchema>;

export const CharacterSpawnAIOptionsSchema = z.object({
  enabled: z.boolean().default(false),
  detailLevel: CharacterSpawnAIDetailLevelSchema.optional(),
  countryHint: z.string().optional(),
  fallbackBuildTemplateId: z
    .preprocess(
      value => normalizeOptionalBuildTemplateId(typeof value === 'string' ? value : undefined),
      z.string().optional(),
    )
    .optional(),
  generateCombatDesign: z.boolean().default(true),
  generatePresenceDesign: z.boolean().default(true),
});
export type CharacterSpawnAIOptions = z.infer<typeof CharacterSpawnAIOptionsSchema>;

const CharacterSpawnMaterializeOptionsSchemaBase = z.object({
  id: z.string().optional(),
  worldBookId: z.string().optional(),
  seedId: z.string().optional(),
  syncToWorldBook: z.boolean().optional(),
  assignWorldBookId: z.boolean().optional(),
});
export const CharacterSpawnMaterializeOptionsSchema = CharacterSpawnMaterializeOptionsSchemaBase;
export type CharacterSpawnMaterializeOptions = z.infer<typeof CharacterSpawnMaterializeOptionsSchemaBase>;

const CharacterSpawnRequestSchemaBase = z.object({
  /** 种子定义驱动：适合敌人、居民、内置可复用 seed 角色。 */
  seedDefinition: CharacterSeedDefinitionSchema.optional(),
  /** 完整档案驱动：适合玩家创角、手写角色、内置角色。 */
  profile: CharacterProfileSchema.optional(),
  /** 机械骨架或骨架提示：完整时可直接生成，不完整时可作为 AI 选骨架提示。 */
  seed: CharacterCompositionSeedSchema.partial().optional(),
  /** 额外叙事/系统覆写。 */
  profileOverrides: CharacterSeedProfileOverridesSchema.default({}),
  entityOptions: CharacterSeedEntityOverridesSchema.default({}),
  presence: CharacterPresenceSchema.partial().default({}),
  /** 显式 patch：用于增量修正和统一补完。 */
  patch: CharacterPatchSchema.default(() => CharacterPatchSchema.parse({})),
  /** 当需要 AI 判骨架或 AI 补完时使用的描述。 */
  description: z.string().optional(),
  ai: CharacterSpawnAIOptionsSchema.default(() => CharacterSpawnAIOptionsSchema.parse({})),
  materializeOptions: CharacterSpawnMaterializeOptionsSchema.default(() =>
    CharacterSpawnMaterializeOptionsSchema.parse({}),
  ),
});
export const CharacterSpawnRequestSchema = CharacterSpawnRequestSchemaBase;
export type CharacterSpawnRequest = z.input<typeof CharacterSpawnRequestSchemaBase>;
export type ResolvedCharacterSpawnRequest = z.output<typeof CharacterSpawnRequestSchemaBase>;

export const CharacterSpawnStrategySchema = z.enum([
  'seed_definition',
  'seed_definition_ai_patch',
  'profile',
  'profile_ai_patch',
  'seed',
  'seed_ai_patch',
  'ai_seed',
]);
export type CharacterSpawnStrategy = z.infer<typeof CharacterSpawnStrategySchema>;
